import api from './api';

/**
 * Normalize a raw API client object to the shape used by the UI.
 * API fields: id, client_name, website_url, client_logo, is_active, created_at
 */
const normalizeClient = (raw) => ({
  id: raw.id,
  name: raw.client_name ?? raw.name ?? '',
  logo: raw.client_logo ?? raw.logo ?? null,
  websiteUrl: raw.website_url ?? raw.websiteUrl ?? '',
  isActive: raw.is_active !== undefined ? Boolean(Number(raw.is_active)) : Boolean(raw.isActive),
  createdAt: raw.created_at ?? raw.createdAt ?? null,
});

/**
 * Build a multipart/form-data payload from UI values.
 * Only appends `client_logo` when a real File object is provided.
 */
const buildFormData = ({ name, websiteUrl, logo, isActive }) => {
  const fd = new FormData();
  fd.append('client_name', name ?? '');
  fd.append('website_url', websiteUrl ?? '');
  fd.append('is_active', isActive ? '1' : '0');
  if (logo instanceof File) {
    fd.append('client_logo', logo);
  }
  return fd;
};

export const clientService = {
  getAll: async (params = {}) => {
    const response = await api.get('/clients', { params });
    const raw = response.data;
    // Support both { data: [...] } and plain array responses
    const list = Array.isArray(raw) ? raw : (raw.data ?? raw.clients ?? []);
    const clients = list.map(normalizeClient);
    return {
      data: {
        clients,
        total: raw.total ?? clients.length,
      },
    };
  },

  getById: async (id) => {
    const response = await api.get(`/clients/${id}`);
    const raw = response.data?.data ?? response.data;
    return { data: normalizeClient(raw) };
  },

  create: async (data) => {
    const fd = buildFormData(data);
    const response = await api.post('/clients', fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const raw = response.data?.data ?? response.data;
    return { data: normalizeClient(raw) };
  },

  update: async (id, data) => {
    const fd = buildFormData(data);
    const response = await api.post(`/clients/update/${id}`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    const raw = response.data?.data ?? response.data;
    return { data: normalizeClient(raw) };
  },

  delete: async (id) => {
    return api.delete(`/clients/${id}`);
  },

  toggleStatus: async (id) => {
    const response = await api.patch(`/clients/toggle/${id}`);
    return response;
  },
};
